import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import type {
  FacialBiometryDoctorItem,
  FacialBiometryListParams,
  FacialBiometryListResponse,
  FacialBiometryValidationResult,
  ValidateFacialBiometryPayload
} from '@/types/facial-biometry.types'
import { BACKEND_TO_FB_STATUS, FB_STATUS_TO_BACKEND } from '@/constants/facial-biometry.constants'

interface RawDoctorItem extends Omit<FacialBiometryDoctorItem, 'status'> {
  status: string
}

interface RawListResponse {
  data: RawDoctorItem[]
  meta: FacialBiometryListResponse['meta']
}

export default class FacialBiometryService extends Service {
  constructor() {
    super()
    this.prefix = '/admin/facial-biometry'
  }

  async listDoctors(params: FacialBiometryListParams): Promise<FacialBiometryListResponse> {
    try {
      const { data } = await request.get<RawListResponse>(
        this.getCompletePath('/doctors'),
        this.buildQueryParams(params)
      )
      return {
        data: data.data.map((item) => ({
          ...item,
          status: BACKEND_TO_FB_STATUS[item.status] ?? BACKEND_TO_FB_STATUS.PENDING
        })),
        meta: data.meta
      }
    } catch (e) {
      throw this.handleError(e, 'Ocorreu um erro ao buscar as biometrias faciais')
    }
  }

  async validate(
    id: number,
    payload: ValidateFacialBiometryPayload
  ): Promise<FacialBiometryValidationResult> {
    try {
      const { data } = await request.patch<FacialBiometryValidationResult>(
        this.getCompletePath(`/${id}/validate`),
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Ocorreu um erro ao validar a biometria facial')
    }
  }

  /**
   * Baixa a foto da biometria via endpoint autenticado do backend. A URL bruta
   * do Azure Blob nunca é exposta ao navegador. O interceptor do axios injeta
   * o `Authorization: Bearer <token>` automaticamente.
   */
  async getPhotoBlob(biometryId: number): Promise<Blob> {
    const { data } = await request.get<Blob>(
      this.getCompletePath(`/${biometryId}/photo`),
      undefined,
      undefined,
      'blob'
    )
    return data
  }

  async notifyDoctor(doctorId: number): Promise<void> {
    await request.post(this.getCompletePath(`/doctors/${doctorId}/notify`), {})
  }

  private buildQueryParams(params: FacialBiometryListParams): Record<string, string | number> {
    const query: Record<string, string | number> = {}

    if (params.page) query.page = params.page
    if (params.limit) query.limit = params.limit
    if (params.sortOrder) query.sortOrder = params.sortOrder
    if (params.search?.trim()) query.search = params.search.trim()
    if (params.status) query.status = FB_STATUS_TO_BACKEND[params.status]
    if (params.scheduleIds?.length) query.scheduleIds = params.scheduleIds.join(',')
    if (params.healthUnitIds?.length) query.healthUnitIds = params.healthUnitIds.join(',')
    if (params.startDate) query.startDate = params.startDate
    if (params.endDate) query.endDate = params.endDate

    return query
  }
}
